import React, { useEffect, useMemo, useState } from 'react';
import { Anchor, Building2, Copy, Edit, ImagePlus, Plus, Store, Trash2, Utensils, Wine, X } from 'lucide-react';
import { addDoc, collection, deleteDoc, doc, getDocs, orderBy, query, serverTimestamp, updateDoc } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';
import LoadingState from '../shared/LoadingState';
import { useToast } from '../shared/ToastProvider';
import './Establishments.css';

const establishmentTypes = [
  { value: 'bar', label: 'Bar', icon: Wine },
  { value: 'restaurant', label: 'Restaurante', icon: Utensils },
  { value: 'kiosk', label: 'Quiosque', icon: Store }
];

const pointOfSaleTypes = establishmentTypes.map(type => type.value);

const menuCategories = [
  'Entradas',
  'Pratos',
  'Lanches',
  'Porções',
  'Bebidas',
  'Sobremesas',
  'Outros'
];

const Establishments = () => {
  const { userData } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [establishments, setEstablishments] = useState([]);
  const [selectedEstablishmentId, setSelectedEstablishmentId] = useState('');
  const [menuItems, setMenuItems] = useState([]);
  const [showEstablishmentModal, setShowEstablishmentModal] = useState(false);
  const [showMenuModal, setShowMenuModal] = useState(false);
  const [editingEstablishment, setEditingEstablishment] = useState(null);
  const [editingMenuItem, setEditingMenuItem] = useState(null);
  const [menuImageFile, setMenuImageFile] = useState(null);
  const [menuImagePreview, setMenuImagePreview] = useState('');
  const [uploadingMenuImage, setUploadingMenuImage] = useState(false);
  const [copiedLink, setCopiedLink] = useState('');
  const [establishmentForm, setEstablishmentForm] = useState({
    name: '',
    type: 'restaurant',
    location: '',
    description: '',
    active: true
  });
  const [menuForm, setMenuForm] = useState({
    name: '',
    category: 'Pratos',
    price: '',
    description: '',
    imageUrl: '',
    active: true
  });

  const companyId = userData?.role === 'collaborator'
    ? userData?.currentCompanyId
    : userData?.companyId;

  const selectedEstablishment = useMemo(() => (
    establishments.find(item => item.id === selectedEstablishmentId)
  ), [establishments, selectedEstablishmentId]);

  useEffect(() => {
    if (companyId) {
      loadEstablishments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    if (companyId && selectedEstablishmentId) {
      loadMenuItems(selectedEstablishmentId);
    } else {
      setMenuItems([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, selectedEstablishmentId]);

  const loadEstablishments = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, `companies/${companyId}/establishments`), orderBy('createdAt', 'desc')));
      const data = snap.docs
        .map(item => ({ id: item.id, ...item.data() }))
        .filter(item => pointOfSaleTypes.includes(item.type));
      setEstablishments(data);
      setSelectedEstablishmentId(current => current || data[0]?.id || '');
    } catch (error) {
      console.error('Erro ao carregar pontos de venda:', error);
      const permissionMessage = error?.code === 'permission-denied'
        ? 'Sem permissão para carregar pontos de venda. Publique as regras atualizadas do Firestore.'
        : 'Erro ao carregar pontos de venda.';
      showToast(permissionMessage, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadMenuItems = async (establishmentId) => {
    try {
      const snap = await getDocs(query(
        collection(db, `companies/${companyId}/establishments/${establishmentId}/menuItems`),
        orderBy('createdAt', 'desc')
      ));
      setMenuItems(snap.docs.map(item => ({ id: item.id, ...item.data() })));
    } catch (error) {
      console.error('Erro ao carregar cardápio:', error);
      showToast('Erro ao carregar cardápio.', { type: 'error' });
      setMenuItems([]);
    }
  };

  const getType = (type) => establishmentTypes.find(item => item.value === type) || establishmentTypes[0];
  const supportsMenu = (type) => pointOfSaleTypes.includes(type);
  const formatCurrency = (value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value) || 0);

  const orderLink = (establishment) => {
    const slug = userData?.companySlug || userData?.slug || companyId;
    return `${window.location.origin}/pedido/${slug}/${establishment.id}`;
  };

  const copyOrderLink = async (establishment) => {
    const link = orderLink(establishment);
    await navigator.clipboard.writeText(link);
    setCopiedLink(establishment.id);
    setTimeout(() => setCopiedLink(''), 2000);
  };

  const openEstablishmentModal = (establishment = null) => {
    setEditingEstablishment(establishment);
    setEstablishmentForm(establishment ? {
      name: establishment.name || '',
      type: establishment.type || 'restaurant',
      location: establishment.location || '',
      description: establishment.description || '',
      active: establishment.active !== false
    } : {
      name: '',
      type: 'restaurant',
      location: '',
      description: '',
      active: true
    });
    setShowEstablishmentModal(true);
  };

  const saveEstablishment = async (event) => {
    event.preventDefault();
    if (!establishmentForm.name.trim()) {
      showToast('Informe o nome do ponto de venda.', { type: 'warning' });
      return;
    }

    const payload = {
      ...establishmentForm,
      name: establishmentForm.name.trim(),
      location: establishmentForm.location.trim(),
      description: establishmentForm.description.trim(),
      companyId,
      updatedAt: serverTimestamp()
    };

    if (editingEstablishment) {
      await updateDoc(doc(db, `companies/${companyId}/establishments`, editingEstablishment.id), payload);
    } else {
      const created = await addDoc(collection(db, `companies/${companyId}/establishments`), {
        ...payload,
        createdAt: serverTimestamp()
      });
      setSelectedEstablishmentId(created.id);
    }

    setShowEstablishmentModal(false);
    setEditingEstablishment(null);
    showToast('Ponto de venda salvo com sucesso.', { type: 'success' });
    loadEstablishments();
  };

  const deleteEstablishment = async (establishment) => {
    if (!window.confirm(`Excluir "${establishment.name}"? O cardápio vinculado não aparecerá mais para pedidos.`)) return;
    await deleteDoc(doc(db, `companies/${companyId}/establishments`, establishment.id));
    if (selectedEstablishmentId === establishment.id) {
      setSelectedEstablishmentId('');
    }
    showToast('Ponto de venda excluído com sucesso.', { type: 'success' });
    loadEstablishments();
  };

  const openMenuModal = (item = null) => {
    setEditingMenuItem(item);
    setMenuImageFile(null);
    setMenuImagePreview(item?.imageUrl || '');
    setMenuForm(item ? {
      name: item.name || '',
      category: item.category || 'Pratos',
      price: item.price?.toString() || '',
      description: item.description || '',
      imageUrl: item.imageUrl || '',
      active: item.active !== false
    } : {
      name: '',
      category: 'Pratos',
      price: '',
      description: '',
      imageUrl: '',
      active: true
    });
    setShowMenuModal(true);
  };

  const handleMenuImageSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showToast('Selecione um arquivo de imagem.', { type: 'warning' });
      event.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast('A imagem deve ter no máximo 5 MB.', { type: 'warning' });
      event.target.value = '';
      return;
    }
    if (menuImagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(menuImagePreview);
    }
    setMenuImageFile(file);
    setMenuImagePreview(URL.createObjectURL(file));
  };

  const uploadMenuImage = async () => {
    if (!menuImageFile) return menuForm.imageUrl || '';
    const extension = menuImageFile.name.split('.').pop()?.toLowerCase() || 'jpg';
    const imageRef = ref(
      storage,
      `companies/${companyId}/menu-items/${selectedEstablishmentId}/${Date.now()}.${extension}`
    );
    await uploadBytes(imageRef, menuImageFile, { contentType: menuImageFile.type });
    return getDownloadURL(imageRef);
  };

  const saveMenuItem = async (event) => {
    event.preventDefault();
    const price = parseFloat(menuForm.price);
    if (!selectedEstablishmentId || !menuForm.name.trim() || Number.isNaN(price) || price <= 0) {
      showToast('Informe nome e preço válido.', { type: 'warning' });
      return;
    }

    setUploadingMenuImage(true);
    let imageUrl;
    try {
      imageUrl = await uploadMenuImage();
    } catch (error) {
      console.error('Erro ao enviar imagem do item:', error);
      showToast('Não foi possível enviar a imagem. Verifique sua conexão.', { type: 'error' });
      setUploadingMenuImage(false);
      return;
    }

    const payload = {
      name: menuForm.name.trim(),
      category: menuForm.category,
      price,
      description: menuForm.description.trim(),
      imageUrl,
      active: menuForm.active,
      companyId,
      establishmentId: selectedEstablishmentId,
      updatedAt: serverTimestamp()
    };

    try {
      const menuPath = `companies/${companyId}/establishments/${selectedEstablishmentId}/menuItems`;
      if (editingMenuItem) {
        await updateDoc(doc(db, menuPath, editingMenuItem.id), payload);
      } else {
        await addDoc(collection(db, menuPath), { ...payload, createdAt: serverTimestamp() });
      }

      setShowMenuModal(false);
      setEditingMenuItem(null);
      setMenuImageFile(null);
      showToast('Item do cardápio salvo com sucesso.', { type: 'success' });
      loadMenuItems(selectedEstablishmentId);
    } catch (error) {
      console.error('Erro ao salvar item do cardápio:', error);
      showToast('Não foi possível salvar o item.', { type: 'error' });
    } finally {
      setUploadingMenuImage(false);
    }
  };

  const deleteMenuItem = async (item) => {
    if (!window.confirm(`Excluir "${item.name}" do cardápio?`)) return;
    await deleteDoc(doc(db, `companies/${companyId}/establishments/${selectedEstablishmentId}/menuItems`, item.id));
    showToast('Item do cardápio excluído com sucesso.', { type: 'success' });
    loadMenuItems(selectedEstablishmentId);
  };

  if (loading) {
    return <LoadingState title="Carregando pontos de venda" subtitle="Buscando operações e cardápios" />;
  }

  return (
    <div className="est-container">
      <div className="est-header">
        <div>
          <h1>Pontos de venda</h1>
          <p>Cadastre bares, restaurantes e quiosques da empresa.</p>
        </div>
        <button className="est-primary-button" onClick={() => openEstablishmentModal()}>
          <Plus size={18} />
          Novo Cadastro
        </button>
      </div>

      <div className="est-layout">
        <section className="est-list">
          {establishments.map(establishment => {
            const type = getType(establishment.type);
            const Icon = type.icon;
            return (
              <article
                key={establishment.id}
                className={`est-card ${selectedEstablishmentId === establishment.id ? 'is-selected' : ''}`}
                onClick={() => setSelectedEstablishmentId(establishment.id)}
              >
                <div className="est-card-main">
                  <Icon size={24} />
                  <div>
                    <h3>{establishment.name}</h3>
                    <span>{type.label}{establishment.location ? ` · ${establishment.location}` : ''}</span>
                  </div>
                </div>
                {establishment.description && <p>{establishment.description}</p>}
                <div className="est-card-actions">
                  {supportsMenu(establishment.type) && (
                    <button onClick={(event) => { event.stopPropagation(); copyOrderLink(establishment); }}>
                      <Copy size={15} />
                      {copiedLink === establishment.id ? 'Copiado' : 'Link pedido'}
                    </button>
                  )}
                  <button onClick={(event) => { event.stopPropagation(); openEstablishmentModal(establishment); }}>
                    <Edit size={15} />
                    Editar
                  </button>
                  <button className="danger" onClick={(event) => { event.stopPropagation(); deleteEstablishment(establishment); }}>
                    <Trash2 size={15} />
                  </button>
                </div>
              </article>
            );
          })}

          {establishments.length === 0 && (
            <div className="est-empty">
              <Building2 size={44} />
              <p>Nenhum ponto de venda cadastrado.</p>
            </div>
          )}
        </section>

        <section className="est-menu-panel">
          {selectedEstablishment ? (
            <>
              <div className="est-menu-header">
                <div>
                  <span>Cadastro selecionado</span>
                  <h2>{selectedEstablishment.name}</h2>
                </div>
                {supportsMenu(selectedEstablishment.type) && (
                  <button className="est-primary-button" onClick={() => openMenuModal()}>
                    <Plus size={18} />
                    Item do Cardápio
                  </button>
                )}
              </div>

              {supportsMenu(selectedEstablishment.type) ? (
                <>
                  <div className="est-link-box">
                    <Anchor size={18} />
                    <span>{orderLink(selectedEstablishment)}</span>
                    <button onClick={() => copyOrderLink(selectedEstablishment)}>
                      {copiedLink === selectedEstablishment.id ? 'Copiado' : 'Copiar'}
                    </button>
                  </div>

                  <div className="est-menu-grid">
                    {menuItems.map(item => (
                      <article key={item.id} className="est-menu-item">
                        {item.imageUrl && (
                          <img className="est-menu-image" src={item.imageUrl} alt={item.name} />
                        )}
                        <div>
                          <span>{item.category}</span>
                          <h3>{item.name}</h3>
                          {item.description && <p>{item.description}</p>}
                        </div>
                        <strong>{formatCurrency(item.price)}</strong>
                        <div className="est-menu-actions">
                          <button onClick={() => openMenuModal(item)}>Editar</button>
                          <button className="danger" onClick={() => deleteMenuItem(item)}><Trash2 size={15} /></button>
                        </div>
                      </article>
                    ))}
                  </div>

                  {menuItems.length === 0 && <p className="est-muted">Cadastre os itens do cardápio para liberar pedidos pelo link.</p>}
                </>
              ) : null}
            </>
          ) : (
            <div className="est-empty">
              <Building2 size={44} />
              <p>Selecione ou cadastre um ponto de venda.</p>
            </div>
          )}
        </section>
      </div>

      {showEstablishmentModal && (
        <div className="modal-overlay" onClick={() => setShowEstablishmentModal(false)}>
          <div className="modal-content est-modal" onClick={(event) => event.stopPropagation()}>
            <div className="est-modal-header">
              <h2>{editingEstablishment ? 'Editar Cadastro' : 'Novo Cadastro'}</h2>
              <button onClick={() => setShowEstablishmentModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={saveEstablishment}>
              <div className="form-group">
                <label>Nome</label>
                <input value={establishmentForm.name} onChange={(event) => setEstablishmentForm({ ...establishmentForm, name: event.target.value })} required />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Tipo</label>
                  <select value={establishmentForm.type} onChange={(event) => setEstablishmentForm({ ...establishmentForm, type: event.target.value })}>
                    {establishmentTypes.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Local / Referência</label>
                  <input value={establishmentForm.location} onChange={(event) => setEstablishmentForm({ ...establishmentForm, location: event.target.value })} placeholder="Ex: Deck, praia, salão 1" />
                </div>
              </div>
              <div className="form-group">
                <label>Descrição</label>
                <textarea rows="3" value={establishmentForm.description} onChange={(event) => setEstablishmentForm({ ...establishmentForm, description: event.target.value })} />
              </div>
              <label className="est-checkbox">
                <input type="checkbox" checked={establishmentForm.active} onChange={(event) => setEstablishmentForm({ ...establishmentForm, active: event.target.checked })} />
                Ativo
              </label>
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowEstablishmentModal(false)}>Cancelar</button>
                <button type="submit" className="btn-primary">Salvar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showMenuModal && (
        <div className="modal-overlay" onClick={() => setShowMenuModal(false)}>
          <div className="modal-content est-modal" onClick={(event) => event.stopPropagation()}>
            <div className="est-modal-header">
              <h2>{editingMenuItem ? 'Editar Item' : 'Novo Item do Cardápio'}</h2>
              <button onClick={() => setShowMenuModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={saveMenuItem}>
              <div className="form-group">
                <label>Nome</label>
                <input value={menuForm.name} onChange={(event) => setMenuForm({ ...menuForm, name: event.target.value })} required />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Categoria</label>
                  <select value={menuForm.category} onChange={(event) => setMenuForm({ ...menuForm, category: event.target.value })}>
                    {menuCategories.map(category => <option key={category} value={category}>{category}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Preço</label>
                  <input type="number" step="0.01" min="0" value={menuForm.price} onChange={(event) => setMenuForm({ ...menuForm, price: event.target.value })} required />
                </div>
              </div>
              <div className="form-group">
                <label>Descrição</label>
                <textarea rows="3" value={menuForm.description} onChange={(event) => setMenuForm({ ...menuForm, description: event.target.value })} />
              </div>
              <div className="form-group">
                <label>Imagem do item</label>
                <div className="est-image-upload">
                  {menuImagePreview ? (
                    <div className="est-image-preview">
                      <img src={menuImagePreview} alt="Pré-visualização do item" />
                      <button
                        type="button"
                        onClick={() => {
                          if (menuImagePreview.startsWith('blob:')) {
                            URL.revokeObjectURL(menuImagePreview);
                          }
                          setMenuImageFile(null);
                          setMenuImagePreview('');
                          setMenuForm(current => ({ ...current, imageUrl: '' }));
                        }}
                        aria-label="Remover imagem"
                        title="Remover imagem"
                      >
                        <X size={17} />
                      </button>
                    </div>
                  ) : (
                    <label className="est-image-upload-label">
                      <input type="file" accept="image/*" onChange={handleMenuImageSelect} />
                      <ImagePlus size={30} />
                      <span>Selecionar imagem</span>
                      <small>JPG, PNG ou WebP, até 5 MB</small>
                    </label>
                  )}
                </div>
              </div>
              <label className="est-checkbox">
                <input type="checkbox" checked={menuForm.active} onChange={(event) => setMenuForm({ ...menuForm, active: event.target.checked })} />
                Ativo no cardápio
              </label>
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowMenuModal(false)} disabled={uploadingMenuImage}>Cancelar</button>
                <button type="submit" className="btn-primary" disabled={uploadingMenuImage}>
                  {uploadingMenuImage ? 'Enviando imagem...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Establishments;
